<template>
  <div class="managed-companies-view flex flex-col">
    <page-header :title="tr('Managed Companies')" />

    <div class="flex flex-col gap-4 py-5">
      <div class="relative max-w-sm">
        <search-icon class="pointer-events-none absolute top-1/2 left-3 z-10 size-4 -translate-y-1/2 text-muted-foreground" />
        <ui-input
          ref="searchInput"
          v-model="searchTerm"
          type="search"
          class="pl-9"
          :placeholder="tr('Search') + '...'"
          :aria-label="tr('Search')"
        />
      </div>

      <empty-screen
        v-if="filteredList.length === 0"
        icon="search"
        :title="tr('No results match your current search criteria')"
        :flag-text="tr('Modify search')"
        :description="tr('Currently there are no items that match your criteria. The items you\'re looking for might appear here later, or try different parameters to see currently available items.')"
      />

      <div
        v-else
        role="table"
        :aria-label="tr('Managed Companies')"
        class="flex flex-col gap-2"
      >
        <div role="row" class="grid items-center gap-x-4 grid-cols-[minmax(0,1fr)_2rem] md:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1fr)_2rem] lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.2fr)_2rem] px-4 text-xs font-semibold text-muted-foreground max-md:hidden">
          <span role="columnheader" :aria-sort="sortDirection === 'asc' ? 'ascending' : 'descending'">
            <button
              type="button"
              data-slot="sort-button"
              class="inline-flex cursor-pointer items-center gap-1 hover:text-foreground"
              @click="() => sortDirection = sortDirection === 'asc' ? 'desc' : 'asc'"
            >
              {{ tr('Name') }}
              <arrow-up-icon v-if="sortDirection === 'asc'" class="size-3.5" />
              <arrow-down-icon v-else class="size-3.5" />
            </button>
          </span>
          <span role="columnheader">{{ tr('Billing name') }}</span>
          <span role="columnheader">{{ tr('Owner') }}</span>
          <span role="columnheader" class="max-lg:hidden">{{ tr('Email') }}</span>
          <span role="columnheader" />
        </div>

        <div
          v-for="row in sortedList"
          :key="'managed-company--' + row.id"
          role="row"
          :class="[
            'grid items-center gap-x-4 grid-cols-[minmax(0,1fr)_2rem] md:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1fr)_2rem] lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.2fr)_2rem] glass-tile rounded-xl p-4 text-sm md:py-2.5',
            row.id === $root.currentCompany.id ? 'pointer-events-none opacity-60' : 'cursor-pointer transition-shadow duration-300 ease-in-out hover:[--tile-glow:color-mix(in_oklab,var(--primary)_40%,transparent)]',
          ]"
          :aria-current="row.id === $root.currentCompany.id || null"
          @click="() => select(row.id)"
        >
          <div role="cell" class="flex min-w-0 flex-col gap-0.5">
            <span class="flex min-w-0 items-center gap-1.5">
              <star-icon
                v-if="row.id === $root.loggedInUser.company.id"
                v-tooltip="tr('Current Company')"
                class="size-3.5 shrink-0 fill-current text-primary"
              />
              <span :class="[ 'truncate', !row.name && !row.billing_company ? 'text-muted-foreground italic' : 'font-medium text-foreground' ]">
                {{ row.name || row.billing_company || row.id }}
              </span>
            </span>
            <span v-if="row.owner" class="truncate text-xs text-muted-foreground md:hidden">{{ row.owner.fullname }} &middot; {{ row.owner.email }}</span>
          </div>
          <div role="cell" class="truncate text-muted-foreground max-md:hidden">{{ row.billing_company || '–' }}</div>
          <div role="cell" class="truncate text-muted-foreground max-md:hidden">{{ row.owner ? row.owner.fullname : '–' }}</div>
          <div role="cell" class="truncate text-muted-foreground max-lg:hidden">{{ row.owner ? row.owner.email : '–' }}</div>
          <div role="cell" class="flex justify-end">
            <template v-if="row.id !== $root.currentCompany.id">
              <loader-circle-icon v-if="loadingCompanyId === row.id" class="size-4 animate-spin text-primary" />
              <arrow-link v-else :label="row.name || row.billing_company || row.id" @click.stop="() => select(row.id)" />
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  ArrowDown,
  ArrowUp,
  LoaderCircle,
  Search,
  Star,
} from '@lucide/vue';

import ArrowLink from '@/ui/components/ArrowLink.vue';
import EmptyScreen from '@/ui/components/EmptyScreen.vue';
import PageHeader from '@/ui/components/PageHeader.vue';

export default {
  name: 'ManagedCompaniesView',
  components: {
    ArrowDownIcon: ArrowDown,
    ArrowUpIcon: ArrowUp,
    ArrowLink,
    EmptyScreen,
    LoaderCircleIcon: LoaderCircle,
    SearchIcon: Search,
    StarIcon: Star,
    PageHeader,
  },
  data() {
    return {
      searchTerm: '',
      loadingCompanyId: null,
      sortDirection: 'asc',
    };
  },
  computed: {
    list() {
      const list =  [ ...this.$root.loggedInUser.company.managed_companies ];
      list.sort((a, b) => (a.name ?? a.id).localeCompare(b.name ?? b.id));
      list.unshift(this.$root.loggedInUser.company);

      return list;
    },
    // By name, with the user's own company pinned to the top.
    sortedList() {
      const ownId = this.$root.loggedInUser.company.id;
      const direction = this.sortDirection === 'asc' ? 1 : -1;
      const name = company => company.name || company.billing_company || company.id;

      return [ ...this.filteredList ].sort((a, b) => {
        if (a.id === ownId) {
          return -1;
        }
        if (b.id === ownId) {
          return 1;
        }

        return direction * String(name(a)).localeCompare(String(name(b)), this.$root.language);
      });
    },
    filteredList() {
      let filteredList = [ ...this.list ];

      if (this.searchTerm) {
        const term = this.searchTerm.toLowerCase();

        filteredList = this.list.filter(company => {
          const ownerName = company.owner?.fullname ?? '';
          const reverseOwnerName = company.owner ? (company.owner.lastname + ' ' + company.owner.firstname) : '';
          const ownerEmail = company.owner?.email ?? '';
          return (
            (company.name && company.name.toLowerCase().includes(term)) ||
            (company.billing_company && company.billing_company.toLowerCase().includes(term)) ||
            ownerName.toLowerCase().includes(term) ||
            reverseOwnerName.toLowerCase().includes(term) ||
            ownerEmail.toLowerCase().includes(term)
          );
        });

        return filteredList;
      }

      return filteredList;
    },
  },
  mounted() {
    this.$refs.searchInput.$el.focus();
  },
  methods: {
    select(companyId) {
      if (
        this.loadingCompanyId !== null
        || this.$root.currentCompany.id === companyId
      ) {
        return;
      }

      this.loadingCompanyId = companyId;

      this.$store.commit('auth/selectCompany', companyId);

      setTimeout(() => window.location = '/dashboard', 250);
    },
  },
};
</script>
